import React, { useState } from 'react';
import { View, TextInput, ScrollView, StyleSheet, TouchableOpacity, Alert, Image, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../../theme/ThemeContext';
import { CustomPicker } from '../../components/common/CustomPicker';
import { ClubsStackParamList } from '../../types/navigation';
import { CLUB_CATEGORIES, CLUB_CITIES } from '../../data/mockClubsData';
import { COUNTRIES_WITH_CITIES } from '../../data/countriesAndCities';
import ThemedText from '../../components/common/ThemedText';
import { createClub } from '../../lib/api/clubs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { storage } from '../../utils/storage';
import { apiBaseUrl } from '../../config/env';

type CreateClubNavigationProp = StackNavigationProp<ClubsStackParamList, 'CreateClub'>;

const CreateClubScreen: React.FC = () => {
  const navigation = useNavigation<CreateClubNavigationProp>();
  const { theme } = useTheme();
  
  const [clubName, setClubName] = useState('');
  const [category, setCategory] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [about, setAbout] = useState('');
  const [rules, setRules] = useState('');
  const [coverPhotoUri, setCoverPhotoUri] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [annualFee, setAnnualFee] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleBack = () => {
    navigation.goBack();
  };

  const uploadImageToServer = async (uri: string): Promise<string> => {
    const token = await storage.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }

    const formData = new FormData();
    const filename = uri.split('/').pop() || 'cover_photo.jpg';
    const match = /\.(\w+)$/.exec(filename);
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    // Handle web differently
    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();
      const file = new File([blob], filename, { type });
      formData.append('image', file);
    } else {
      formData.append('image', {
        uri,
        name: filename,
        type,
      } as any);
    }

    const response = await fetch(`${apiBaseUrl}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json();
    
    if (!data.success || !data.url) {
      throw new Error(data.message || 'Failed to upload image');
    }

    return data.url;
  };

  const handlePickCoverPhoto = async () => {
    try {
      // Photo picker on Android 13+ uses system UI - no READ_MEDIA_* permissions needed

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [16, 9],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        try {
          // Show loading state
          setIsCreating(true);
          // Upload to cloud storage
          const cloudUrl = await uploadImageToServer(result.assets[0].uri);
          setCoverPhotoUri(cloudUrl);
          showSuccessToast('Cover photo uploaded successfully');
        } catch (error: any) {
          console.error('Error uploading image:', error);
          showErrorToast(error.message || 'Failed to upload cover photo');
        } finally {
          setIsCreating(false);
        }
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  const handleCreateClub = async () => {
    // Validate fields
    if (!clubName.trim()) {
      Alert.alert('Error', 'Please enter a club name');
      return;
    }
    if (!category) {
      Alert.alert('Error', 'Please select a category');
      return;
    }
    if (!country) {
      Alert.alert('Error', 'Please select a country');
      return;
    }
    if (!city) {
      Alert.alert('Error', 'Please select a city');
      return;
    }
    if (!description.trim()) {
      Alert.alert('Error', 'Please enter a description');
      return;
    }

    try {
      setIsCreating(true);
      
      await createClub({
        name: clubName.trim(),
        category,
        city,
        description: description.trim(),
        about: about.trim() || undefined,
        rules: rules.trim() || undefined,
        coverPhotoUrl: coverPhotoUri || undefined,
        isPaid,
        annualFee: isPaid && annualFee ? Number(annualFee) : undefined,
      });

      showSuccessToast('Club created successfully! You are now the admin.');
      navigation.navigate('ClubsHome');
    } catch (error) {
      console.error('Error creating club:', error);
      showErrorToast(error instanceof Error ? error.message : 'Failed to create club. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const isFormValid = clubName.trim() && category && city && description.trim() && (!isPaid || (annualFee && Number(annualFee) > 0));

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Create Club</ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.form}>
          {/* Cover Photo */}
          <TouchableOpacity 
            style={styles.coverPhotoContainer}
            onPress={handlePickCoverPhoto}
            activeOpacity={0.7}
          >
            {coverPhotoUri ? (
              <Image source={{ uri: coverPhotoUri }} style={styles.coverPhoto} />
            ) : (
              <View style={[styles.coverPhotoPlaceholder, { backgroundColor: theme.primary }]}>
                <Ionicons name="camera" size={40} color="#000000" />
                <ThemedText style={styles.coverPhotoText}>Add Cover Photo</ThemedText>
              </View>
            )}
          </TouchableOpacity>

          {/* Club Name */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Club Name *</ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
              placeholder="Enter club name"
              value={clubName}
              onChangeText={setClubName}
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* Category */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Category *</ThemedText>
            <CustomPicker
              selectedValue={category || undefined}
              onValueChange={(value) => setCategory(value || '')}
              items={[
                { label: 'Select a category', value: undefined },
                ...CLUB_CATEGORIES.map((cat) => ({
                  label: cat,
                  value: cat,
                })),
              ]}
              placeholder="Select a category"
            />
          </View>

          {/* Country */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Country *</ThemedText>
            <CustomPicker
              selectedValue={country || undefined}
              onValueChange={(value) => {
                setCountry(value || '');
                setCity(''); // Reset city when country changes
              }}
              items={[
                { label: 'Select a country', value: undefined },
                ...COUNTRIES_WITH_CITIES.map((c) => ({
                  label: c.name,
                  value: c.name,
                })),
              ]}
              placeholder="Select a country"
              searchable={true}
              searchPlaceholder="Search countries..."
            />
          </View>

          {/* City */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>City *</ThemedText>
            <CustomPicker
              selectedValue={city || undefined}
              onValueChange={(value) => setCity(value || '')}
              items={[
                { label: country ? 'Select a city' : 'Select a country first', value: undefined },
                ...(country 
                  ? (COUNTRIES_WITH_CITIES.find(c => c.name === country)?.cities || []).map((cityName) => ({
                      label: cityName,
                      value: cityName,
                    }))
                  : []
                ),
              ]}
              placeholder="Select a city"
              searchable={true}
              searchPlaceholder="Search cities..."
              disabled={!country}
            />
          </View>

          {/* Description */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Description *</ThemedText>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
              placeholder="Describe your club and its purpose"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* About */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>About</ThemedText>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
              placeholder="Tell members about your club, its mission, and what makes it special"
              value={about}
              onChangeText={setAbout}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* Rules */}
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Rules</ThemedText>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: theme.card, color: theme.text, borderColor: theme.border }]}
              placeholder="Set guidelines and rules for club members"
              value={rules}
              onChangeText={setRules}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* Membership Type */}
          <View style={[styles.fieldContainer, { borderTopWidth: 1, borderTopColor: theme.border }]}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Membership Type *</ThemedText>
            <View style={styles.toggleRow}>
              <TouchableOpacity
                style={[styles.toggleOption, !isPaid && { backgroundColor: theme.primary, borderColor: theme.primary }]}
                onPress={() => { setIsPaid(false); setAnnualFee(''); }}
                activeOpacity={0.7}
              >
                <Ionicons name="lock-open-outline" size={16} color={!isPaid ? '#000000' : theme.textSecondary} />
                <ThemedText style={[styles.toggleOptionText, !isPaid && { color: '#000000', fontWeight: '700' }]}>Free</ThemedText>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.toggleOption, isPaid && { backgroundColor: theme.primary, borderColor: theme.primary }]}
                onPress={() => setIsPaid(true)}
                activeOpacity={0.7}
              >
                <Ionicons name="card-outline" size={16} color={isPaid ? '#000000' : theme.textSecondary} />
                <ThemedText style={[styles.toggleOptionText, isPaid && { color: '#000000', fontWeight: '700' }]}>Paid</ThemedText>
              </TouchableOpacity>
            </View>
          </View>

          {/* Annual Fee (only for paid clubs) */}
          {isPaid && (
            <View style={styles.fieldContainer}>
              <ThemedText style={[styles.label, { color: theme.text }]}>Annual Fee (INR) *</ThemedText>
              <View style={[styles.feeInputRow, { backgroundColor: theme.card, borderColor: theme.border }]}>
                <ThemedText style={[styles.feePrefix, { color: theme.textSecondary }]}>₹</ThemedText>
                <TextInput
                  style={[styles.feeInput, { color: theme.text }]}
                  placeholder="e.g. 2000"
                  value={annualFee}
                  onChangeText={(v) => setAnnualFee(v.replace(/[^0-9]/g, ''))}
                  keyboardType="number-pad"
                  placeholderTextColor={theme.textSecondary}
                />
                <ThemedText style={[styles.feeSuffix, { color: theme.textSecondary }]}>/year</ThemedText>
              </View>
            </View>
          )}

          {/* Info Note */}
          <View style={[styles.infoBox, { backgroundColor: theme.primary + '20', borderColor: theme.primary }]}>
            <Ionicons name="information-circle" size={20} color={theme.primary} />
            <ThemedText style={[styles.infoText, { color: theme.text }]}>
              You will automatically become the admin of this club. The club will be publicly visible immediately.
            </ThemedText>
          </View>
        </View>
      </ScrollView>

      {/* Bottom Action Bar */}
      <View style={[styles.bottomBar, { backgroundColor: theme.backgroundSecondary, borderTopColor: theme.border }]}>
        <TouchableOpacity
          style={[styles.cancelButton, { borderColor: theme.border }]}
          onPress={handleBack}
          activeOpacity={0.7}
        >
          <ThemedText style={[styles.cancelButtonText, { color: theme.text }]}>Cancel</ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: isFormValid ? theme.primary : theme.disabled }]}
          onPress={handleCreateClub}
          activeOpacity={0.7}
          disabled={!isFormValid || isCreating}
        >
          <ThemedText style={styles.createButtonText}>
            {isCreating ? 'Creating...' : 'Create Club'}
          </ThemedText>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  form: {
    paddingBottom: 100,
  },
  coverPhotoContainer: {
    marginBottom: 16,
  },
  coverPhoto: {
    height: 150,
    width: '100%',
    resizeMode: 'cover',
  },
  coverPhotoPlaceholder: {
    height: 150,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverPhotoText: {
    fontSize: 14,
    color: '#000000',
    marginTop: 8,
    fontWeight: '600',
  },
  fieldContainer: {
    padding: 16,
    marginBottom: 12,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    borderWidth: 1,
  },
  textArea: {
    minHeight: 100,
    paddingTop: 12,
  },
  pickerContainer: {
    backgroundColor: '#F3F2EF',
    borderRadius: 8,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
  },
  infoBox: {
    flexDirection: 'row',
    padding: 16,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    gap: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  bottomBar: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    borderWidth: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  createButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  createButtonDisabled: {},
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  toggleRow: {
    flexDirection: 'row',
    gap: 12,
  },
  toggleOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: '#E0E0E0',
  },
  toggleOptionText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#666666',
  },
  feeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  feePrefix: {
    fontSize: 18,
    fontWeight: '700',
    marginRight: 6,
  },
  feeInput: {
    flex: 1,
    fontSize: 15,
  },
  feeSuffix: {
    fontSize: 13,
    marginLeft: 6,
  },
});

export default CreateClubScreen;
